import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTypedTranslation } from '@app/hooks';
import { setExceptionHandler } from '@app/store/actions';
import { USUrl, genericErrorToast, emailInputRegex, tenantTypeOptions } from '@app/helpers';
import { ModalActionsComponent, PasslySelect, PasslyTextField } from '@app/components';
import { FormControl, FormHelperText } from '@material-ui/core';
import { tenantsService } from '@app/services';
import { AxiosResponse } from 'axios';
import { toast } from 'react-toastify';
import { TenantTypes } from '@app/models';

const validator = require('validator');

export interface IReceivedData {
    dialogOpen: Function;
}

export const AddTenantDialog = (props: IReceivedData) => {
    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();
    const shellConfig = useSelector((state: any) => state.basic.shellConfig);

    const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
    const [tenantSuffix, setTenantSuffix] = useState<string>('');
    const [homeRealm, setHomeRealm] = useState<string>('');
    const [homeRealmInvalid, setHomeRealmInvalid] = useState<boolean>(false);
    const [companyName, setCompanyName] = useState<string>('');
    const [companyNameInvalid, setCompanyNameInvalid] = useState<boolean>(false);
    const [displayName, setDisplayName] = useState<string>('');
    const [displayNameInvalid, setDisplayNameInvalid] = useState<boolean>(false);
    const [email, setEmail] = useState<string>('');
    const [emailInvalid, setEmailInvalid] = useState<boolean>(false);
    const [username, setUsername] = useState<string>('');
    const [usernameInvalid, setUsernameInvalid] = useState<boolean>(false);
    const [usingType, setUsingType] = useState<TenantTypes | ''>('');
    const [formInvalid, setFormInvalid] = useState<boolean>(true);

    const regexCheckHomeRealmInput = new RegExp('^[a-z0-9-]*$');
    const regexCheckNameInput = new RegExp('^[A-Za-z0-9 _]*[A-Za-z0-9][A-Za-z0-9 _]*$');
    const regexUsernameInput = new RegExp('[.a-zA-Z0-9_-]*@*[.a-zA-Z0-9_-]*');

    useEffect(() => {
        dispatch(setExceptionHandler(true));

        if (shellConfig.configs.tenantSuffix) {
            setTenantSuffix('.' + shellConfig.configs.homeRealmSuffix);
        } else {
            setTenantSuffix(USUrl);
        }
    }, []);

    useEffect(() => {
        if (
            homeRealm === '' ||
            homeRealmInvalid ||
            companyName === '' ||
            companyNameInvalid ||
            displayName === '' ||
            displayNameInvalid ||
            email === '' ||
            emailInvalid ||
            username === '' ||
            usernameInvalid ||
            !usingType
        ) {
            setFormInvalid(true);
        } else {
            setFormInvalid(false);
        }
    }, [homeRealm, companyName, displayName, email, username, usingType]);

    const onFormChange = (e: any) => {
        const { name, value } = e.target;

        switch (name) {
            case 'homeRealm':
                setHomeRealm(value);
                break;
            case 'companyName':
                setCompanyName(value);
                break;
            case 'displayName':
                setDisplayName(value);
                break;
            case 'email':
                setEmail(value);
                setUsername(value);
                break;
            case 'username':
                setUsername(value);
                break;
            case 'usingType':
                setUsingType(value);
                break;
            default:
                break;
        }
    };

    const onHomeRealmChange = (e: any) => {
        onFormChange(e);

        const homeRealmChosen = e.target.value.trim();

        if (!validator.isEmpty(homeRealmChosen) && regexCheckHomeRealmInput.test(homeRealmChosen)) {
            setHomeRealmInvalid(false);
        } else {
            setHomeRealmInvalid(true);
        }
    };

    const onCompanyNameChange = (e: any) => {
        onFormChange(e);

        const companyNameChosen = e.target.value.trim();

        if (!validator.isEmpty(companyNameChosen) && regexCheckNameInput.test(companyNameChosen)) {
            setCompanyNameInvalid(false);
        } else {
            setCompanyNameInvalid(true);
        }
    };

    const onDisplayNameChange = (e: any) => {
        onFormChange(e);

        const displayNameChosen = e.target.value.trim();

        if (!validator.isEmpty(displayNameChosen) && regexCheckNameInput.test(displayNameChosen)) {
            setDisplayNameInvalid(false);
        } else {
            setDisplayNameInvalid(true);
        }
    };

    const onEmailChange = (e: any) => {
        onFormChange(e);

        const emailChosen = e.target.value.trim();

        if (!validator.isEmpty(emailChosen) && emailInputRegex.test(emailChosen)) {
            setEmailInvalid(false);
        } else {
            setEmailInvalid(true);
        }
    };

    const onUsernameChange = (e: any) => {
        onFormChange(e);

        const usernameChosen = e.target.value.trim();

        if (!validator.isEmpty(usernameChosen) && regexUsernameInput.test(usernameChosen)) {
            setUsernameInvalid(false);
        } else {
            setUsernameInvalid(true);
        }
    };

    const handleSaveBtn = (e: any) => {
        e.preventDefault();

        // validations
        setErrorMessage('');
        tenantsService.getTenantByHomeRealm(homeRealm, handleHomeRealmCheck, genericErrorToast);
    };

    const handleHomeRealmCheck = (response: AxiosResponse) => {
        if (response.data.value && response.data.value.length > 0) {
            // home realm is taken
            setErrorMessage(translation('INVALID_TAKEN_HOME_REALM'));
            setFormInvalid(true);
        } else {
            // save tenant
            let jsonObject: any = {};
            jsonObject.homeRealm = homeRealm;
            jsonObject.usingType = usingType;
            jsonObject.company = {};
            jsonObject.company.name = companyName;
            jsonObject.requestor = {};
            jsonObject.requestor.displayName = displayName;
            jsonObject.requestor.emailAddress = email;
            jsonObject.requestor.principalName = username;
            jsonObject.requestor.attributes = [];

            tenantsService.addTenant(jsonObject, genericErrorToast, handleAddTenantSuccess);

            toast(translation('TENANT_BEING_CREATED'));

            props.dialogOpen(false);
        }
    };

    const handleAddTenantSuccess = () => {
        props.dialogOpen(false);
    };

    const handleCancel = () => {
        props.dialogOpen(false);
    };

    return (
        <>
            <form
                method='post'
                className='modal__form modal__form-row-margin'
                tabIndex={0}
                onKeyPress={(e: React.KeyboardEvent<HTMLFormElement>) => {
                    if (e.key === 'Enter' && !homeRealmInvalid && homeRealm !== '') {
                        e.preventDefault();
                        handleSaveBtn(e);
                    }
                }}
            >
                <div className='modal__form-row'>
                    <PasslyTextField
                        fullWidth
                        name='homeRealm'
                        label='HOME_REALM_LABEL'
                        value={homeRealm}
                        onChange={onHomeRealmChange}
                        id='homeRealm-input'
                        error={homeRealmInvalid}
                        helperText={homeRealmInvalid && translation('ORG_HOMEREALM_INVALID')}
                        inputProps={{ maxLength: 250 }}
                        required
                    />
                </div>
                <div className='modal__form-row'>
                    <PasslyTextField
                        fullWidth
                        name='companyName'
                        label={'COMPANY_NAME'}
                        value={companyName}
                        onChange={onCompanyNameChange}
                        id='companyName-input'
                        inputProps={{ maxLength: 250 }}
                        required
                        error={companyNameInvalid}
                        helperText={companyNameInvalid && translation('INVALID_COMPANY_DISPLAY_NAME')}
                    />
                </div>
                <div className='modal__form-row'>
                    <PasslyTextField
                        fullWidth
                        name='displayName'
                        label={'DISPLAY_NAME'}
                        value={displayName}
                        onChange={onDisplayNameChange}
                        id='displayName-input'
                        inputProps={{ maxLength: 250 }}
                        required
                        error={displayNameInvalid}
                        helperText={displayNameInvalid && translation('INVALID_DISPLAY_NAME')}
                    />
                </div>
                <div className='modal__form-row'>
                    <PasslyTextField
                        fullWidth
                        name='email'
                        label={'EMAIL_ADDR'}
                        value={email}
                        onChange={onEmailChange}
                        id='email-input'
                        inputProps={{ maxLength: 250 }}
                        required
                        error={emailInvalid}
                        helperText={emailInvalid && translation('INVALID_EMAIL')}
                    />
                </div>
                <div className='modal__form-row'>
                    <PasslyTextField
                        fullWidth
                        name='username'
                        label={'USERS_NAME'}
                        value={username}
                        onChange={onUsernameChange}
                        id='username-input'
                        inputProps={{ maxLength: 250 }}
                        required
                        error={usernameInvalid}
                        helperText={usernameInvalid && translation('INVALID_PRINCIPAL_NAME')}
                    />
                </div>
                <div className='modal__body--row'>
                    <FormControl required fullWidth error={!usingType}>
                        <PasslySelect
                            fullWidth
                            name='usingType'
                            value={usingType}
                            id='usingType-input'
                            onChange={onFormChange}
                            label={'TENANT_MME_MSP'}
                            options={tenantTypeOptions}
                            appendTo={document.body}
                            required={true}
                        />
                        {usernameInvalid && <FormHelperText>{translation('EMPTY_FIELD_ERROR')}</FormHelperText>}
                    </FormControl>
                </div>
            </form>

            {errorMessage !== undefined && errorMessage !== '' && <div className='modal__error'>{errorMessage}</div>}

            <ModalActionsComponent
                primaryBtn={translation('ADD_TENANT')}
                secondaryBtn={translation('CANCEL')}
                onClickPrimary={handleSaveBtn}
                onClickSecondary={handleCancel}
                disabled={formInvalid}
                id='addTenant'
            />
        </>
    );
};
